<template>
  <div
    class="filtered-posts-list"
    v-observe-visibility="visibilityChanged"
    :class="['type-' + postType, baseAnimation, { 'has-search': search }]"
  >
    <SearchForm
      @changed="searchChange"
      :placeholder="searchPlaceholder"
      class="animate-delay-02"
      v-if="search"
      v-observe-visibility="visibilityChanged"
      :class="[baseAnimation]"
    />
    <CategoriesFilter
      class="animate-delay-02"
      v-observe-visibility="visibilityChanged"
      :class="[baseAnimation]"
      :key="taxonomy"
      v-if="filters"
      :taxonomy="taxonomy"
      :multiple="filtersMultiple"
      @filters="updateFilters"
    >
      <div v-if="currentPostType === 'events'" class="past-events">
        <button
          class="btn swap-filter"
          :class="{ active: pastEvents }"
          @click="pastEvents = !pastEvents"
        >
          Se tidligere arrangementer
        </button>
      </div>
    </CategoriesFilter>
    <transition-group
      name="company"
      tag="section"
      v-if="elementsToShow && elementsToShow.length"
      class="article-list archive-list padding_large animate-delay-03 filters-resoultats"
    >
      <component
        :is="itemComponent(article)"
        v-for="(article, i) in elementsToShow"
        :key="article.id"
        v-if="i < perPage"
        :id="article.id"
      />
    </transition-group>
    <section
      v-else-if="!isPageLoaded"
      v-observe-visibility="visibilityChanged"
      :class="[baseAnimation]"
      class="article-list archive-list padding_large"
    >
      <component :is="componentModule" />
      <component :is="componentModule" />
      <component :is="componentModule" />
      <component :is="componentModule" />
      <component :is="componentModule" />
      <component :is="componentModule" />
    </section>
    <section v-else class="article-list archive-not-found padding_large">
      <ModuleHeading>{{ notFoundText }}</ModuleHeading>
    </section>
    <button
      v-observe-visibility="visibilityChangedLoadMore"
      @click="loadMore"
      v-show="elementsToShow.length > perPage"
      class="btn load-more"
      :class="'load-' + postType"
    >
      <span class="icon" v-html="svgIcons.load"> </span>
      <span class="text">Last inn flere {{ loadMoreText }}</span>
    </button>
  </div>
</template>

<script>
import _debounce from "lodash.debounce";
import _orderby from "lodash.orderby";
import moment from "moment";

import SinglePost from "@/components/blocks/SinglePost";
import SingleEvent from "@/components/blocks/SingleEvent";
import SearchBlock from "@/components/blocks/SearchBlock";
import StyledIcon from "@/components/elements/StyledIcon";
import SingleHelp from "@/components/blocks/SingleHelp";
import SingleVideo from "@/components/blocks/SingleVideo";
import CategoriesFilter from "@/components/blocks/CategoriesFilter";
import SearchForm from "@/components/blocks/SearchForm";

import { mapGetters } from "vuex";

const normalizeSearchText = text =>
  text
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const plainText = value =>
  typeof value === "string" && value ? value.stripHtml() : "";

// Text worth matching per ACF module layout (image/gallery/news modules
// carry no searchable text). Modules can arrive as an object instead of an
// array: when ACF skips a row the indexes get a gap and PHP's json_encode
// emits {"0":..,"1":..,"3":..} (the Inspirasjon page does this).
const moduleTexts = modules => {
  const texts = [];
  Object.values(modules || {}).forEach(module => {
    if (!module) return;
    switch (module.acf_fc_layout) {
      case "links":
        texts.push(module.title);
        break;
      case "alert":
        texts.push(module.alert_title);
        break;
      case "logo_box":
        (module.box || []).forEach(box => box && texts.push(box.content));
        break;
      case "text_block":
        texts.push(module.text_header);
        (module.text_columns || []).forEach(
          column => column && texts.push(column.content)
        );
        break;
      case "text_image":
        if (module.text_side) {
          texts.push(module.text_side.title, module.text_side.content);
        }
        break;
      case "collapse":
        (module.blocks || []).forEach(
          block => block && texts.push(block.title, block.content)
        );
        break;
      case "cta_with_form":
        texts.push(module.title, module.description);
        break;
    }
  });
  return texts;
};

// Plain lowercased { title, body } a post is matched against, cached per
// post object because stripping HTML on every keystroke is the expensive
// part. Posts replaced in the store (refetch, password unlock) get a fresh
// entry.
const searchTextCache = new WeakMap();
const postSearchText = post => {
  let text = searchTextCache.get(post);
  if (text === undefined) {
    const acf = post.acf || {};
    text = {
      title: normalizeSearchText(plainText(post.title && post.title.rendered)),
      body: normalizeSearchText(
        [
          post.excerpt && post.excerpt.rendered,
          post.content && post.content.rendered,
          acf.excerpt,
          ...moduleTexts(acf.modules)
        ]
          .map(plainText)
          .join(" ")
      )
    };
    searchTextCache.set(post, text);
  }
  return text;
};

export default {
  name: "filtered-posts-list",
  props: {
    taxonomy: {
      type: String,
      default: "category"
    },
    searchPage: {
      type: Boolean,
      default: false
    },
    postType: {
      type: String,
      default: "post"
    },
    // merged-list mode: concatenates several post types into one list
    // (postType still drives CSS classes and the skeleton placeholders)
    postTypes: {
      type: Array,
      default: null
    },
    filtersMultiple: {
      type: Boolean,
      default: false
    },
    filtersLogic: {
      type: String,
      default: "or" // "or": at least one active category matches, "and": all must match
    },
    filters: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: ""
    },
    search: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeFilters: [],
      perPageBase: 12,
      perPage: 12,
      perLoad: 6,
      searchString: "",
      pastEvents: false
    };
  },
  watch: {
    elementsToShow() {
      //this.nullLoadMore()
    },
    $route: {
      deep: true,
      handler(to) {
        this.searchChange(to.query.search ? to.query.search : "");
      }
    }
  },
  computed: {
    ...mapGetters([
      "getPosts",
      "getEvents",
      "isPageLoaded",
      "getPostsData",
      "currentPostType"
    ]),
    postsToDisplay() {
      let posts = null;
      let type = this.postType.toLowerCase();
      if (this.postTypes && this.postTypes.length) {
        posts = [];
        this.postTypes.forEach(postType => {
          const typePosts = this.getPosts(null, postType);
          if (typePosts) posts.push(...typePosts);
        });
        if (!posts.length) posts = null;
      } else if (type === "posttypes") {
        const postData = this.getPostsData;
        posts = [];
        Object.keys(postData).forEach(function(item) {
          if (item !== "attachment") posts.push(...postData[item]);
        });
      } else {
        if (this.postType !== "events") {
          posts = this.getPosts(null, this.postType);
        } else {
          posts = this.getEvents([], this.pastEvents);
        }
      }

      // "Prioritet" (ACF number, lowest first, empty last) then newest first.
      // Only for video/inspirasjon lists so other archives keep their order.
      if (
        posts &&
        (this.postTypes || type === "video" || type === "inspirasjon")
      ) {
        posts = _orderby(
          posts,
          [
            post =>
              post.acf && post.acf.priority
                ? Number(post.acf.priority)
                : Infinity,
            // wp/v2-fetched copies have "date" instead of "post_date"
            post => Number(moment(post.post_date || post.date).format("x"))
          ],
          ["asc", "desc"]
        );
      }
      return posts;
    },
    loadMoreText() {
      let text = "";
      let type = this.postType.toLowerCase();
      if (this.postTypes && this.postTypes.length) return "innlegg";
      switch (type) {
        case "events":
          text = "arrangementer";
          break;
        case "help":
          text = "hjelpesider";
          break;
        case "video":
          text = "videoer";
          break;
        case "posttypes":
          text = "";
          break;
        default:
          text = "artikler";
          break;
      }
      return text;
    },
    elementsToShow() {
      const VM = this;
      if (!VM.postsToDisplay) return false;
      let element = VM.postsToDisplay;
      let type = this.postType.toLowerCase();
      if (VM.activeFilters.length && VM.filters) {
        if (type !== "posttypes") {
          element = element.filter(it => {
            let canShow = false;
            if (it.acf && it.acf._categories && it.acf._categories.length) {
              if (VM.filtersLogic === "and") {
                canShow = VM.activeFilters.every(
                  taxID => it.acf._categories.indexOf(taxID) !== -1
                );
              } else {
                it.acf._categories.forEach(taxID => {
                  if (VM.activeFilters.indexOf(taxID) !== -1) {
                    canShow = true;
                  }
                });
              }
            }
            return canShow;
          });
        } else {
          element = element.filter(it => {
            return VM.activeFilters.indexOf(it.post_type) !== -1;
          });
        }
      }

      // Must never throw: a render error makes Vue keep the previous
      // (unfiltered) list on screen, so every search "returns everything".
      const searchString = normalizeSearchText(VM.searchString || "");
      if (searchString !== "" && element && element.length > 0) {
        // 0 = title match, 1 = category name, 2 = body text, -1 = no match
        const rank = it => {
          const text = postSearchText(it);
          if (text.title.indexOf(searchString) !== -1) return 0;
          const categories =
            it.acf && Array.isArray(it.acf._categories)
              ? it.acf._categories
              : [];
          if (
            categories.some(
              id =>
                VM.categoryNames[id] &&
                VM.categoryNames[id].indexOf(searchString) !== -1
            )
          )
            return 1;
          return text.body.indexOf(searchString) !== -1 ? 2 : -1;
        };
        // best matches first; the stable sort keeps the list's own order
        // (e.g. "Prioritet") within each group
        element = _orderby(
          element
            .map(it => ({ it, rank: rank(it) }))
            .filter(hit => hit.rank !== -1),
          "rank"
        ).map(hit => hit.it);
      }

      return element;
    },
    componentModule() {
      let component = SinglePost;
      let type = this.postType.toLowerCase();
      switch (type) {
        case "events":
          component = SingleEvent;
          break;
        case "posttypes":
          component = SearchBlock;
          break;
        case "help":
          component = SingleHelp;
          break;
        case "video":
          component = SingleVideo;
          break;
      }
      return component;
    },
    notFoundText() {
      return this.wpOptions && this.wpOptions.not_found_text
        ? this.wpOptions.not_found_text
        : "Ingenting funnet, vennligst prøv et annet filter";
    },
    // term id -> searchable name, across all taxonomies (term ids are unique
    // site-wide), so searching "steg 1" also finds posts in that category
    categoryNames() {
      const names = {};
      const taxonomies = this.$store.state.taxonomies || {};
      Object.keys(taxonomies).forEach(tax => {
        (taxonomies[tax] || []).forEach(term => {
          if (term) names[term.id] = normalizeSearchText(plainText(term.name));
        });
      });
      return names;
    }
  },
  components: {
    CategoriesFilter,
    SearchForm,
    StyledIcon
  },
  methods: {
    // per-item resolution for the merged list: videos keep their card/lightbox
    // (bulk payload posts have post_type, wp/v2-fetched copies have type)
    itemComponent(article) {
      const type = article && (article.post_type || article.type);
      if (type === "video") return SingleVideo;
      return this.componentModule;
    },
    searchChange(val) {
      this.searchString = val;
      this.updateRoute();
    },
    updateRoute: _debounce(function() {
      //            this.$router.replace({ query: { ...this.$route.query, search: this.searchString} })
      let query = {};
      if (this.activeFilters) {
        query.kategori = this.activeFilters;
      }
      if (this.searchString) {
        query.search = this.searchString;
      }
      this.$router.push({ query });
    }, 100),
    visibilityChangedLoadMore(isVisivle) {
      if (isVisivle) this.loadMore();
    },
    loadMore() {
      if (this.elementsToShow.length > this.perPage)
        this.perPage += this.perLoad;
    },
    nullLoadMore() {
      this.perPage = this.perPageBase;
    },
    updateFilters(filters) {
      this.activeFilters = filters;
      this.updateRoute();
    }
  }
};
</script>

<style lang="scss">
@import "./../../assets/scss/mixins.scss";
@import "./../../assets/scss/vars.scss";
.company {
  transform-origin: 10% 50%;
  backface-visibility: hidden;
  &-move {
    transition: all 600ms ease-in-out 50ms;
  }
  &-enter-active {
    transition: all 300ms ease-out;
  }

  &-leave-active {
    transition: all 200ms ease-in;
    position: absolute;
    z-index: 0;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }
  &-enter {
    transform: scale(0.9);
  }
}
.categoreis-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .past-events .swap-filter {
    margin-left: 20px;
    background-color: #fff;
    color: $fontColor;
    transition: all 0.15s ease-in-out;
    border: 2px solid $dark;
    &.active {
      background-color: $dark;
      color: #fff;
    }
    &:hover {
      background-color: gray;
      color: #fff;
    }
  }
}

.filtered-posts-list {
  margin-bottom: 20px;
  .article-list {
    margin-left: -12px;
    margin-right: -12px;
    margin-bottom: -25px;
    .block-single-post {
      @include mquery(767px) {
        max-width: 50%;
      }
      @include mquery(460px) {
        max-width: 100%;
      }
    }
  }
  .btn.load-more {
    margin: 0 auto 50px;
    margin-top: 25px;
    display: block;
    background-color: $purple;
    &.load-post {
      background-color: #fff;
      color: $fontColor;
      svg g {
        fill: $fontColor !important;
      }
    }
  }
  .single-event-block {
    width: 50%;
    padding-left: 12px;
    padding-right: 12px;
    margin-bottom: 25px;
    @include mquery(767px) {
      max-width: 100%;
      width: 100%;
    }
  }
}
</style>
